
/**
 * Entry flow for creating an enriched location recommendation from the nav menu:
 * pick a place via PlacePicker (My Trips / Saved / Search drill-down), then open
 * RecommendThisPlaceModal — same end state as the list timeline heart.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PlacePicker from '@/components/PlacePicker/PlacePicker';
import RecommendThisPlaceModal from '@/components/RecommendThisPlaceModal/RecommendThisPlaceModal';
import ResponsiveOverlay from '@/components/ResponsiveOverlay';
import type Location from '@/models/location';
import type { PlacePickerSelectSource } from '@/components/PlacePicker/PlacePicker';
import { WebAnalytics } from '@/lib/analytics';
import { EVENT_RECOMMEND_PLACE_LOCATION_SELECTED } from '@/lib/analyticsEvents';
import { useRecommendationCache } from '@/hooks/useRecommendationCache';
import { debug } from '@/utils/logger';

export interface RecommendFromPostsFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RecommendFromPostsFlowModal: React.FC<RecommendFromPostsFlowModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [pickedLocation, setPickedLocation] = useState<Location | null>(null);
  const { addRecommendation: addRecToCache } = useRecommendationCache();

  const handleFlowClose = useCallback(() => {
    setPickedLocation(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      setPickedLocation(null);
    }
  }, [isOpen]);

  const handleSelectPlace = useCallback((location: Location, source?: PlacePickerSelectSource) => {
    WebAnalytics.track(EVENT_RECOMMEND_PLACE_LOCATION_SELECTED, {
      location_id: location._id ?? 'unknown',
      location_name: location.name ?? 'unknown',
      source: 'recommend-from-posts-nav',
      picker_source: source ?? 'unknown',
    });
    debug('[RecommendFromPostsFlow][RecommendAPlace] place selected → opening RecommendThisPlaceModal', {
      locationId: location._id,
      locationName: location.name,
      picker_source: source ?? 'unknown',
      nextStep: 'User submits form → POST /api/v1/recommendation with locationId + enrichment',
    });
    setPickedLocation(location);
  }, []);

  if (!isOpen || typeof document === 'undefined') return null;

  if (pickedLocation) {
    return createPortal(
      <RecommendThisPlaceModal
        isOpen
        onClose={handleFlowClose}
        mode="create"
        location={pickedLocation}
        onRecommended={(rec) => {
          const lid = rec.location_id;
          if (lid) addRecToCache(lid);
        }}
      />,
      document.body,
    );
  }

  return (
    <ResponsiveOverlay
      onClose={handleFlowClose}
      presentation="sheet"
      unwrapped
      keyboardAware
      aria-labelledby="recommend-from-posts-title"
    >
      <div
        className="gt-keyboard-sheet mobile-sheet relative my-auto flex max-h-[92vh] w-full max-w-[560px] flex-col overflow-hidden bg-white p-6 shadow-[0_0_20px_rgba(0,0,0,0.15)] font-manrope min-[768px]:my-0 min-[768px]:p-8"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recommend-from-posts-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent text-gray-700 hover:bg-gray-100"
          onClick={handleFlowClose}
          aria-label="Close"
        >
          ✕
        </button>
        <h2
          id="recommend-from-posts-title"
          className="m-0 mb-4 pr-10 text-center font-lora text-[22px] font-semibold text-gray-900"
        >
          Recommend a place
        </h2>
        <p className="m-0 mb-4 text-center font-manrope text-sm text-gray-600">
          Open one of your trips or saved posts, pick a place, or use Search — then add your recommendation.
        </p>
        <div className="flex h-[min(62vh,560px)] min-h-[280px] flex-col overflow-hidden">
          <PlacePicker
            onSelectPlace={handleSelectPlace}
            city={null}
            initialTab="my-trips"
            searchTabLabel="Search"
          />
        </div>
      </div>
    </ResponsiveOverlay>
  );
};

export default RecommendFromPostsFlowModal;
